//PropTypes
import PropTypes from "prop-types";

/* eslint-disable no-prototype-builtins */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";

// MUI
import { GridActionsCellItem, GridRowModes, useGridApiRef } from "@mui/x-data-grid-premium";
import { Checkbox, FormControl, Stack, SvgIcon, TextField, Typography } from "@mui/material";

// Components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import ReportGroupSelector from "@Settings/components/AdministrationTools/DealerManagementSystem/PublishInventory/Widgets/ReportGroupSelector";

// Icons
import CancelIcon from "@mui/icons-material/Close";
import { SaveIconComponent } from "@/Layouts/components/Tables/DataSmartTable/additional/SaveIconComponent";

// API
import { updateReynoldsInventoryMapping } from "@/Settings/api/AdministrationTools/mutations";

// Helpers
import RowFeedback from "@/Common/helpers/RowFeedback";
import { sanitizeItemsData } from "@Settings/helpers/AdministrationTools/DealerAdministrationTools/publishInventory";

// Constants
import { TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";

const CSS_FOR_CHECKBOX = "!w-[18px] !h-[18px] !mt-[-2px]";

const isEmptyObject = (obj) => {
	return typeof obj === "object" && Object.keys(obj).length === 0;
};

function CheckboxRenderer(props) {
	const { id, value, field, onRenderUpdate, onDataUpdate, rowModesModel, title, row } = props;
	const [checked, setChecked] = useState(false);

	const handleChange = async () => {
		const newValue = !checked;

		const updatedItems = props?.currentData.map((item) => {
			if (item.id === id) {
				return {
					...item,
					[field]: newValue,
				};
			}
			return item;
		});

		if (rowModesModel && rowModesModel[id]?.mode !== "edit") {
			const sanitizedItems = sanitizeItemsData(updatedItems);
			const dataToSave = {
				[title]: sanitizedItems,
			};

			await onDataUpdate(id, dataToSave);
		}

		setChecked(newValue);
		onRenderUpdate(updatedItems);
	};

	useEffect(() => {
		setChecked(value);
		return () => {
			setChecked(false);
		};
	}, [value]);

	return <Checkbox className={CSS_FOR_CHECKBOX} checked={checked} onChange={handleChange} disabled={!row.isEditable} />;
}

CheckboxRenderer.propTypes = {
	row: PropTypes.object,
};

function CheckboxCustomRenderer(props) {
	const { id, value, rowModesModel, onSwitchMode, onRenderUpdate, prevData } = props;
	const [checked, setChecked] = useState(false);
	const currentRowMode = rowModesModel && rowModesModel[id]?.mode ? rowModesModel && rowModesModel[id]?.mode : null;

	useEffect(() => {
		setChecked(props?.initialChecked ?? false);
	}, [props]);

	const handleChange = (checkedValue) => {
		const newValue = checkedValue ?? !checked;
		let updatedItems = props?.currentData.map((item) => {
			if (item.id === id) {
				return {
					...item,
					isCustom: true,
				};
			}
			return item;
		});

		if (rowModesModel && rowModesModel[id]?.mode !== "edit") {
			if (newValue) {
				// set the row in edit mode
				onSwitchMode(id, "edit");
			}
		}

		if (rowModesModel && rowModesModel[id]?.mode === "edit") {
			if (value) {
				updatedItems = prevData;
			}
			onSwitchMode(id, "view");
		}

		setChecked(Boolean(newValue));
		onRenderUpdate(updatedItems);
	};

	if (value && currentRowMode) {
		return (
			<>{Boolean(value) && <Checkbox className={CSS_FOR_CHECKBOX} checked={currentRowMode !== "view"} onChange={handleChange} />}</>
		);
	}

	return <>{Boolean(value) && <Checkbox className={CSS_FOR_CHECKBOX} checked={checked} onChange={() => handleChange(true)} />}</>;
}

function TextFieldRenderer(props) {
	const { id, value, field, onRenderUpdate, rowModesModel, colDef } = props;
	const [inputValue, setInputValue] = useState("");

	const handleChange = (event) => {
		let newValue = event.target.value;
		newValue = newValue.replace(/\s+/g, "").toUpperCase();
		newValue = newValue.replace(/[^\w\s]/gi, "-");

		if (field === "status") {
			const updatedItems = props?.currentData.map((item) => {
				if (item.id === id) {
					return {
						...item,
						status: newValue,
					};
				}
				return item;
			});

			setInputValue(newValue);
			onRenderUpdate(updatedItems);
		}
	};

	useEffect(() => {
		setInputValue(value || "");
		return () => {
			setInputValue();
		};
	}, [value]);

	if (!value || (rowModesModel && rowModesModel[id]?.mode === "edit")) {
		return (
			<FormControl fullWidth>
				<TextField
					variant="outlined"
					value={inputValue}
					inputProps={{
						minLength: props.minLength || 0,
						maxLength: props.maxLength || Infinity,
					}}
					className={clsx({
						"regular-text-input-spiff": true,
					})}
					onChange={handleChange}
				/>
			</FormControl>
		);
	} else {
		return <CustomCell value={value} hasTooltip maxWith={colDef.width} fontSize="14.5px" />;
	}
}

TextFieldRenderer.propTypes = {
	id: PropTypes.string,
	value: PropTypes.string,
	field: PropTypes.string,
	onRenderUpdate: PropTypes.func,
	rowModesModel: PropTypes.object,
	onInputClick: PropTypes.func,
	colDef: PropTypes.object,
};

const SAME_COLUMN_PROPS = {
	resizable: false,
	headerClassName: "table-small-header show-as-column",
};

export default function InventoryMappingTable(props) {
	const [data, setData] = useState([]);
	const apiRef = useGridApiRef();
	const location = useSelector((state) => state.location.location);
	const [rowModesModel, setRowModesModel] = useState({});
	const prevDataRef = useRef();
	const columnVisibilityModel = { _id: false, _isEditable: false };

	/********************************************* MUTATIONS ************************************************/
	const { mutate: saveInventoryMapping } = useMutation({
		mutationFn: (input) => updateReynoldsInventoryMapping(location?.locationId, input),
	});

	const columns = useMemo(() => {
		return [
			{
				field: "_id",
				headerName: "ID",
				filterable: false,
			},
			{
				field: "_isEditable",
				headerName: "",
				filterable: false,
			},
			{
				field: "isCustom",
				headerName: "",
				sortable: false,
				width: 50,
				align: "center",
				...SAME_COLUMN_PROPS,
				renderCell: (params, event) => (
					<CheckboxCustomRenderer
						{...params}
						currentData={data}
						onRenderUpdate={handleRenderUpdate}
						onSwitchMode={handleSwitchMode}
						rowModesModel={rowModesModel}
						prevData={prevDataRef.current}
						title={props.title}
						event={event}
					/>
				),
			},
			{
				field: "status",
				headerName: "Description",
				filterable: true,
				width: 140,
				...SAME_COLUMN_PROPS,
				renderCell: (params) => (
					<TextFieldRenderer
						{...params}
						currentData={data}
						onRenderUpdate={handleRenderUpdate}
						rowModesModel={rowModesModel}
						onInputClick={onInputClick}
						title={props.title}
					/>
				),
			},
			{
				field: "isActive",
				headerName: "Active",
				width: 70,
				align: "center",
				...SAME_COLUMN_PROPS,
				headerClassName: `${SAME_COLUMN_PROPS.headerClassName} place-items-center`,
				headerAlign: "center",
				renderCell: (params) => (
					<CheckboxRenderer
						{...params}
						currentData={data}
						onRenderUpdate={handleRenderUpdate}
						onDataUpdate={handleDataUpdate}
						rowModesModel={rowModesModel}
						title={props.title}
					/>
				),
			},

			{
				field: "mappedName",
				headerName: "Report Group",
				width: 150,
				...SAME_COLUMN_PROPS,
				renderCell: (params) => {
					return (
						<ReportGroupSelector
							{...params}
							currentData={data}
							dmsMappingOptions={props.dmsMappingOptions}
							rowModesModel={rowModesModel}
							selectLabel="--"
							title={props.title}
							// Callbacks
							onDataUpdate={handleDataUpdate}
							onRenderUpdate={handleRenderUpdate}
						/>
					);
				},
			},
			{
				field: "actions",
				headerName: "",
				type: "actions",
				width: 80,
				...SAME_COLUMN_PROPS,
				getActions: (params) => {
					const { id } = params;
					const isInEditMode = rowModesModel[id]?.mode === "edit";
					if (isInEditMode) {
						return [
							<GridActionsCellItem
								key="saveInventoryEditMode"
								icon={<SvgIcon className="!w-[20px] !h-[20px]" component={SaveIconComponent} viewBox={"-3 -3 24 24"} />}
								label="Save"
								onClick={() => handleSaveClick(id)}
							/>,
							<GridActionsCellItem
								key="cancel"
								icon={<CancelIcon />}
								label="Cancel"
								className="textPrimary"
								onClick={() => handleCancelClick(id)}
								color="inherit"
							/>,
						];
					}

					if (params.row.isCustom && params.row.isEditable) {
						return [
							<GridActionsCellItem
								key="saveInventory"
								icon={<SvgIcon className="!w-[20px] !h-[20px]" component={SaveIconComponent} viewBox={"-3 -3 24 24"} />}
								label="Save"
								disabled={true}
							/>,
							<GridActionsCellItem key="cancel" icon={<CancelIcon />} label="Cancel" disabled={true} />,
						];
					}

					return [];
				},
			},
		];
	});

	/********************************************* HANDLERS ************************************************/
	const handleRenderUpdate = (newData) => {
		setData(newData);
	};

	const handleDataUpdate = async (id, newData) => {
		const update = new Promise((resolve, reject) => {
			saveInventoryMapping(newData, {
				onSuccess: () => {
					RowFeedback.skeleton(id, apiRef).success();
					setRowModesModel((prev) => ({
						...prev,
						[id]: { mode: GridRowModes.View },
					}));

					let currentData = Object.values(newData)?.map((items) => {
						return items?.map((item) => ({
							id: uuidv4(),
							isEditable: Boolean(item.isEditable),
							isCustom: Boolean(item.isCustom),
							isActive: Boolean(item.isActive),
							status: item.name,
							mappedName: item.mappedName,
						}));
					})[0];

					prevDataRef.current = currentData;
					setData(currentData);
					resolve(true);
				},
				onError: (error) => {
					RowFeedback.skeleton(id, apiRef).error();
					setRowModesModel((prev) => ({
						...prev,
						[id]: { mode: GridRowModes.View },
					}));
					reject(error);
				},
			});
		});
		setTimeout(() => RowFeedback.skeleton(id, apiRef).display());
		return await update;
	};

	const handleSwitchMode = async (id, mode = "view") => {
		const modeType = mode === "edit" ? GridRowModes.Edit : GridRowModes.View;

		setRowModesModel((prev) => ({
			...prev,
			[id]: { mode: modeType },
		}));
	};

	const onInputClick = (id, mode) => {
		handleSwitchMode(id, mode);
	};

	const handleAddRow = () => {
		// Define a default row initial values.
		const key = uuidv4();
		const newRow = {
			id: key,
			isEditable: true,
			isCustom: true,
			status: null,
			isActive: false,
			mappedName: "--",
		};

		setData((prev) => [...prev, newRow]);

		// Set the new row in edit mode.
		setRowModesModel((prev) => ({
			...prev,
			[key]: { mode: GridRowModes.Edit },
		}));
	};

	const handleSaveClick = async (id) => {
		const rowEdit = apiRef.current.state.editRows;
		const row = Object.values(rowEdit)[0];
		if (row && !isEmptyObject(row)) {
			const keys = Object?.keys(row);
			const errors = keys?.filter((key) => row[key].error || !row[key].value);

			if (!errors?.length) {
				setRowModesModel((prev) => ({
					...prev,
					[id]: { mode: GridRowModes.View },
				}));
			} else if (errors?.length === keys.length) {
				apiRef.current.setState((state) => ({
					...state,
					editRows: {
						...state.editRows,
						[id]: {
							...state.editRows[id],
							...errors.reduce((acc, key) => ({ ...acc, [key]: { ...state.editRows[id][key], error: true } }), {}),
						},
					},
				}));
			}
		} else {
			const sanitizedItems = sanitizeItemsData(data);
			const dataToSave = {
				[props.title]: sanitizedItems,
			};

			const add = new Promise((resolve, reject) => {
				saveInventoryMapping(dataToSave, {
					onSuccess: () => {
						RowFeedback.skeleton(id, apiRef).success();
						setRowModesModel((prev) => ({
							...prev,
							[id]: { mode: GridRowModes.View },
						}));
						prevDataRef.current = data;
						resolve(true);
					},
					onError: (error) => {
						RowFeedback.skeleton(id, apiRef).error();
						setRowModesModel((prev) => ({
							...prev,
							[id]: { mode: GridRowModes.View },
						}));
						reject(error);
					},
				});
			});

			RowFeedback.skeleton(id, apiRef).display();
			return await add;
		}
	};

	const handleDeleteClick = async () => {
		const removeItems = (arr, obj) => {
			let newArr = [...arr];
			const newObj = obj;
			Object.keys(newObj).forEach((key) => {
				let itemIndex = newArr.findIndex((item) => item.id === key && newObj[key].mode === "edit");
				if (itemIndex !== -1) {
					newArr?.splice(itemIndex, 1);
				}
			});
			return newArr;
		};

		const filteredData = removeItems(data, rowModesModel);
		const sanitizedItems = sanitizeItemsData(filteredData);
		const dataToSave = {
			[props.title]: sanitizedItems,
		};

		Object.entries(rowModesModel)?.forEach(([id, { mode }]) => {
			if (mode === "edit") {
				RowFeedback.skeleton(id, apiRef).delete().display();
			}
		});

		const remove = new Promise((resolve, reject) => {
			saveInventoryMapping(dataToSave, {
				onSuccess: () => {
					setData(filteredData);
					prevDataRef.current = filteredData;
					resolve(true);
				},
				onError: (error) => {
					Object.entries(rowModesModel)?.forEach(([id, { mode }]) => {
						if (mode === "edit") {
							RowFeedback.skeleton(id, apiRef).errorDelete();
						}
					});
					reject(error);
				},
			});
		});

		return await remove;
	};

	const handleCancelClick = async (id) => {
		setRowModesModel((prev) => ({
			...prev,
			[id]: { mode: GridRowModes.View },
		}));
	};

	useEffect(() => {
		const newData = props.statuses?.map((item) => ({
			id: item._id,
			isEditable: Boolean(item.isEditable),
			isCustom: Boolean(item.isCustom),
			isActive: Boolean(item.isActive),
			status: item.status,
			mappedName: item.mappedName,
		}));

		prevDataRef.current = newData;
		setData(newData);

		return () => {
			setData([]);
			setRowModesModel({});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.statuses]);

	return (
		<Stack sx={{ minWidth: "480px", minHeight: "1px", width: "100%" }}>
			<Typography className="position-mapping-table-header inventory-section-title">
				{props.title === "new" ? "New Vehicle" : "Pre-Owned Vehicle"}
			</Typography>
			<DataSmartTable
				apiRef={apiRef}
				columns={columns}
				rows={data || []}
				headerHeight={40}
				rowHeight={28}
				editMode="row"
				autoHeight={false}
				rowModesModel={rowModesModel}
				columnVisibilityModel={columnVisibilityModel}
				onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						add: {
							onAddAction: handleAddRow,
							hide: false,
							disabled: false,
						},
						delete: {
							onDeleteAction: handleDeleteClick,
							deleteTitle: "Delete",
							hide: false,
							disabled: !Object.values(rowModesModel).some((value) => value.mode === "edit"),
						},
					},
					noRowsOverlay: {
						title: "No Information Data",
					},
				}}
				hideFooter
				customProps={{
					tableContainerStyle: { width: "480px", height: "100%", minWidth: "480px" },
					tableClassName: {
						"functional-table": true,
						...TAILWIND_FOR_DATA_SMART_TABLE,
						"[&_.toolbar-actions]:!top-[3px]": true,
						"[&_.toolbar-actions]:!mb-[7px]": true,
						"[&_.toolbar-actions>div>button:last-child]:!w-[40px]": true,
						"[&_.toolbar-actions>div>button:last-child]:!h-[40px]": true,
						"!h-full": true,
					},
					hide: false,
					expandableRows: false,
				}}
				checkboxSelection={false}
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection
			/>
		</Stack>
	);
}

InventoryMappingTable.propTypes = {
	title: PropTypes.string,
	statuses: PropTypes.arrayOf(PropTypes.object),
	dmsMappingOptions: PropTypes.arrayOf(PropTypes.object),
};
